import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable, SectionCard, StatCard, StatusChip } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type TableUsage } from '../../api';
import { useAsyncData } from './useAsyncData';

/**
 * Database size and usage, broken down by application.
 *
 * Attribution comes from the module catalogue’s schema mapping, so each application
 * appears as soon as it starts writing data.
 *
 * Attribution comes from the module catalogue's schema mapping. Applications that
 * own a schema appear even before they hold any data, so the breakdown is complete
 * from day one and fills in as each module lands.
 */

/** Binary units, matching how PostgreSQL and disk tooling report size. */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}

/**
 * The drawer's job here is to stop two wrong readings of the numbers: that a module
 * showing "No data yet" is broken, and that this screen is a quota the client is close
 * to exceeding. Neither is true, and both are the obvious inference from a progress bar.
 */
const DATABASE_USAGE_HELP: HelpContent = {
  title: 'Database Usage',
  icon: '💾',
  purpose:
    'Shows <strong>how much storage this install is consuming</strong>, attributed to the application that owns the data. A measurement, not a setting &mdash; nothing on this screen can be changed from this screen.',
  doHere: [
    'Read the <strong>total database size</strong> and how much of it is application data rather than PostgreSQL&rsquo;s own catalogues.',
    'See the breakdown <strong>by application</strong>, with a bar comparing each against the largest.',
    'Open <strong>By table</strong> for the table-level detail, largest first, with data and index sizes separated.',
    'Check the <em>Measured</em> timestamp at the foot &mdash; the figures are taken when the screen loads, so reload to re-measure.',
  ],
  fits: 'Administration &rarr; Database Usage',
  firstTime:
    'Look at <strong>Application data</strong> rather than <strong>Database size</strong>. The first is the client&rsquo;s own content; the second includes PostgreSQL&rsquo;s internal catalogues, which exist in an empty database too.',
  sections: [
    {
      type: 'defs',
      label: 'The four figures at the top',
      items: [
        {
          term: 'Database size',
          def: 'Everything in the database, including PostgreSQL&rsquo;s own system catalogues. This is the number that matches what the hosting bill measures.',
        },
        {
          term: 'Application data',
          def: 'The same measurement with the catalogues excluded &mdash; the client&rsquo;s actual content. Always smaller, and the one to quote when asked "how much data do we have?".',
        },
        {
          term: 'Applications storing data',
          def: 'How many modules have written anything at all. A module that is licensed but unused appears in the list below with <em>No data yet</em>, which is normal, not a fault.',
        },
        {
          term: 'Tables',
          def: 'A count across every schema. It changes when a migration adds or removes a table, not when data is entered.',
        },
      ],
    },
    {
      type: 'html',
      label: 'How storage is attributed to an application',
      html: 'Every product owns a <strong>database schema</strong>, and the module catalogue records which schema belongs to which product. Storage is attributed by that mapping, so <strong>an application appears here as soon as it is catalogued &mdash; before it holds any data</strong>. That is why the breakdown is complete from day one and fills in as each module is used, rather than growing a new row the first time something is saved.',
    },
    {
      type: 'defs',
      label: 'Data, Indexes and Total',
      items: [
        {
          term: 'Data',
          def: 'The rows themselves.',
        },
        {
          term: 'Indexes',
          def: 'The structures that make queries fast. On a table that is searched and sorted a lot, indexes legitimately outweigh the data &mdash; that is them doing their job, not waste.',
        },
        {
          term: 'Total',
          def: 'The two added together, which is what the per-application bars compare.',
        },
      ],
      note:
        'Sizes are in binary units (1 KB = 1024 bytes), matching how PostgreSQL and disk tooling report them.',
    },
    {
      type: 'html',
      label: 'This is not a quota',
      html: 'The bars compare each application <strong>against the largest one here</strong>, not against any limit. A full-looking bar means that application holds the most data in this install &mdash; it does not mean anything is nearly full, and there is no threshold on this screen that triggers anything. Storage limits, where they exist, belong to the hosting arrangement and are not represented here.',
    },
  ],
};

export function DatabaseUsageTab() {
  const { data, loading, error } = useAsyncData(() => api.admin.getDatabaseUsage());

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const columns: GridColDef<TableUsage>[] = [
    { field: 'application', headerName: 'Application', width: 200 },
    { field: 'schema_name', headerName: 'Schema', width: 170 },
    { field: 'table_name', headerName: 'Table', flex: 1, minWidth: 200 },
    {
      field: 'table_bytes',
      headerName: 'Data',
      type: 'number',
      width: 110,
      valueFormatter: (value) => formatBytes(value as number),
    },
    {
      field: 'index_bytes',
      headerName: 'Indexes',
      type: 'number',
      width: 110,
      valueFormatter: (value) => formatBytes(value as number),
    },
    {
      field: 'total_bytes',
      headerName: 'Total',
      type: 'number',
      width: 110,
      valueFormatter: (value) => formatBytes(value as number),
    },
  ];

  const withData = data.applications.filter((a) => a.total_bytes > 0);
  const largest = withData[0]?.total_bytes ?? 1;

  return (
    <Stack spacing={2.5}>
      <HelpPanel help={DATABASE_USAGE_HELP} />

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 1fr))' },
        }}
      >
        <StatCard
          label="Database size"
          value={formatBytes(data.database_bytes)}
          caption={data.database_name}
        />
        <StatCard
          label="Application data"
          value={formatBytes(data.application_bytes)}
          caption="Excludes PostgreSQL catalogs"
        />
        <StatCard
          label="Applications storing data"
          value={`${withData.length} of ${data.applications.length}`}
          caption="The rest have no data yet"
        />
        <StatCard label="Tables" value={data.table_count} caption="Across all schemas" />
      </Box>

      <SectionCard
        title="By application"
        subheader="Storage attributed to the application that owns the schema."
      >
        <Stack spacing={2}>
          {data.applications.map((app) => {
            const empty = app.total_bytes === 0;
            return (
              <Box key={app.application} sx={{ opacity: empty ? 0.6 : 1 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }}>
                    {app.application}
                  </Typography>
                  {empty ? (
                    <StatusChip tone="neutral" label="No data yet" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {app.table_count} {app.table_count === 1 ? 'table' : 'tables'}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontVariantNumeric: 'tabular-nums', width: 90, textAlign: 'right' }}
                  >
                    {formatBytes(app.total_bytes)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={empty ? 0 : Math.min(100, (app.total_bytes / largest) * 100)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            );
          })}
        </Stack>
      </SectionCard>

      <SectionCard title="By table" subheader="Largest first." disableContentPadding>
        <DataTable
          rows={data.tables}
          columns={columns}
          getRowId={(row) => `${row.schema_name}.${row.table_name}`}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </SectionCard>

      <Typography variant="caption" color="text.secondary">
        Measured {new Date(data.captured_at).toLocaleString()}.
      </Typography>
    </Stack>
  );
}
